import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { BaseComponent } from '@app/finxone-web-frontend/app/Modules/shared/widgets/base/base.component';
import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountdownService } from '@app/finxone-web-frontend/app/lib/services/countdown-service/countdown-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { CtaButtonSignalService } from '@app/finxone-web-frontend/app/lib/services/cta-button-signal-service/cta-button-signal.service';
import { ExchangeService } from '@app/finxone-web-frontend/app/lib/services/exchange-service/exchange-service.service';
import {
  FormActionState,
  FormActionType,
} from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import {
  buildHandleBarModel,
  GenericHandleBarModel,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { exchangeQuoteSkeleton } from '@finxone-platform/shared/default-templates';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService, IconCacheService } from '@finxone-platform/shared/services';
import {
  baseCssConfigStyle,
  BaseCssProperties,
  ButtonActionType,
  ExchangeQuotePayload,
  ExchangeQuoteResponse,
  ExchangeQuoteSummary,
  FieldAttribute,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { EMPTY, Observable, of, Subject, Subscription, switchMap, take, takeUntil, tap } from 'rxjs';

@Component({
  standalone: false,
  selector: 'finxone-platform-exchange-quote-widget',
  templateUrl: './exchange-quote-widget.component.html',
  styleUrl: './exchange-quote-widget.component.scss',
})
export class ExchangeQuoteWidgetComponent extends BaseComponent implements OnInit, OnDestroy {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  @Select(FormActionState.getFormActionStateWithId(FormActionTypeEnum.CURRENCY_EXCHANGE))
  formAction!: Observable<FormActionType>;

  public exchangeQuoteSummary: ExchangeQuoteSummary;
  public exchangeQuoteResponse: ExchangeQuoteResponse;
  private quoteExpiration: number;
  private exchangeQuotePayload: ExchangeQuotePayload;
  public companyName: string;
  public childContentStyle: baseCssConfigStyle;
  private content = '';
  public renderedContent = '';
  private baseHandleBarModel: GenericHandleBarModel;
  public templateFunction: HandlebarsTemplateDelegate<any>;
  public countdown: string;
  public useQuoteCountdown: boolean = true;
  private templateSubscription$: Subscription;

  constructor(
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    private configService: ConfigService,
    private store: Store,
    private exchangeService: ExchangeService,
    private location: Location,
    private alertHandlerService: AlertHandlerService,
    public pageConfig: PageConfigurationProvider,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private countdownService: CountdownService,
    private ctaButtonSignalService: CtaButtonSignalService,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.dataIn) {
      this.attri = this.dataIn.attri ?? null;
      this.onLoadWidgetConfiguration();
      this.determineCountdownVisibility();
      this.initCountdown();
      this.getCompanyDetails();
    }
    if (this.isSkeletonPreview) {
      this.renderHandlebarTemplate();
      this.exchangeQuoteResponse =
        this.dataIn.attri?.widgetProperties?.['skeletonPreviewData'] ??
        (exchangeQuoteSkeleton as unknown as ExchangeQuoteResponse);
      return;
    }

    this.formAction
      .pipe(
        take(1),
        tap(() => {
          this.renderHandlebarTemplate();
          this.exchangeQuoteResponse =
            this.dataIn.attri?.widgetProperties?.['skeletonPreviewData'] ??
            (exchangeQuoteSkeleton as unknown as ExchangeQuoteResponse);
        }),
        switchMap((formValues: FormActionType) => {
          if (
            formValues?.formData &&
            formValues?.formData['buyAccountId'] &&
            formValues?.formData['sellAccountId'] &&
            formValues?.formData['amount']
          ) {
            this.exchangeQuotePayload = formValues.formData as ExchangeQuotePayload;
            return this.exchangeService.postExchangeQuote(this.exchangeQuotePayload);
          } else {
            if (this.configService.getActiveConfigType().isWorkflow) {
              // For mocking in the workflow ui delegate to the mocked exchange service
              return this.exchangeService.postExchangeQuote(this.exchangeQuotePayload);
            } else {
              this.alertHandlerService.showAlertFn('error', 'There are no accounts selected for a quote.');

              // go back to the previous screen
              this.location.back();
              return EMPTY;
            }
          }
        }),
      )
      .subscribe(
        (response: ExchangeQuoteResponse) => {
          this.setExchangeQuoteData(response);
          this.renderHandlebarTemplate();
          if (this.useQuoteCountdown) {
            this.countdownService.startCountdown(this.quoteExpiration);
          }
        },
        () => {
          console.error('Failed to get exchange quote, returning to previous screen');
          this.location.back();
        },
      );
  }

  private determineCountdownVisibility(): void {
    // Could add other provider-specific logic here in the future
    // Or could fetch flag from widget properties if set by builders
    const provider = this.store.selectSnapshot(ProjectSettingsState.getProjectServiceProvider);
    if (provider === 'andaria') {
      this.useQuoteCountdown = false;
      // Hide the re-quote button since there's no countdown expiry
      this.ctaButtonSignalService.setHideButtonSignal({
        [ButtonActionType.EXCHANGE_RE_QUOTE]: true,
      });
    }
  }

  private get isSkeletonPreview() {
    return (
      this.configService.getActiveConfigType().isWorkflow &&
      this.dataIn.attri.widgetProperties?.['isSkeletonPreview']
    );
  }

  private updateRenderedContent(countdown: string) {
    this.renderedContent = this.createTemplateData(countdown);
  }

  private renderHandlebarTemplate() {
    // Clean up previous subscription to prevent duplicates
    if (this.templateSubscription$) this.templateSubscription$.unsubscribe();

    this.content = this.attri?.widgetProperties?.textContent;
    this.templateFunction = Handlebars.compile(this.content);

    this.templateSubscription$ = buildHandleBarModel(
      this.store,
      this.content,
      this.countriesCurrenciesService,
      this.iconCacheService,
    )
      .pipe(
        take(1),
        tap((model) => (this.baseHandleBarModel = model)),
        switchMap(() => (this.useQuoteCountdown ? this.countdownService.countdown$ : of(''))),
        takeUntil(this.destroy$),
        tap((countdown) => {
          if (this.useQuoteCountdown) {
            this.countdown = countdown;
            if (countdown === '00') {
              this.ctaButtonSignalService.setSignal({ [ButtonActionType.EXCHANGE_RE_QUOTE]: true });
            }
          }
          this.updateRenderedContent(countdown);
        }),
      )
      .subscribe();
  }

  private createTemplateData(countdown: string) {
    if (this.exchangeQuoteResponse) {
      return this.templateFunction({
        ...this.baseHandleBarModel,
        exchangeQuote: {
          ...this.exchangeQuoteResponse,
          feeAmountPlusSellAmount:
            this.exchangeQuoteResponse.feeAmount + this.exchangeQuoteResponse.sellAmount,
          buyAmountMinusFeeAmount:
            this.exchangeQuoteResponse.buyAmount - this.exchangeQuoteResponse.feeAmount,
          companyName: this.companyName,
          countdown,
        },
      });
    } else {
      return this.templateFunction({
        ...this.baseHandleBarModel,
      });
    }
  }

  private getCompanyDetails() {
    this.configService
      .getSystemName()
      .pipe(take(1))
      .subscribe((systemName) => {
        this.companyName = systemName;
      });
  }

  private setExchangeQuoteData(response: ExchangeQuoteResponse) {
    this.exchangeQuoteSummary = {
      buyCurrency: response.buyCurrency,
      buyAmount: response.buyAmount,
      sellCurrency: response.sellCurrency,
      sellAmount: response.sellAmount,
      feeAmount: response.feeAmount,
      feeCurrency: response.feeCurrency,
      fixedSide: response.fixedSide ?? 'buy',
    };
    this.exchangeQuoteResponse = response;
  }

  private initCountdown() {
    if (!this.useQuoteCountdown) return;
    this.quoteExpiration = this.attri['field_attributes']?.find(
      (field: FieldAttribute) => field.name === 'quoteExpiration',
    ).value;
  }

  private onLoadWidgetConfiguration(): void {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.BOX_SHADOW,
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_FONT_STYLE,
      BaseCssProperties.COLOR,
      BaseCssProperties.BORDER,
    ]);

    this.childContentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_FONT_STYLE,
      BaseCssProperties.COLOR,
    ]);
  }

  ngOnDestroy() {
    if (this.useQuoteCountdown) this.countdownService.stopCountdown();
    this.templateSubscription$?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
